import { getCurrentAuthUser } from '@/services/authSession';
import { isUnauthorizedPlatformApiError, platformApiFetch } from '@/services/platformApi/client';
import { fetchUserProfileByEmailWithFields } from "@/services/profileService";
import { debugLogger } from '@/utils/debug';

export interface MinimalUser { id?: string }
export type NavigateFn = (path: string) => void;

/**
 * Verify if a user is an admin
 * @param userId The user ID to check
 * @returns True if the user is an admin, false otherwise
 */
export const verifyAdmin = async (userId?: string): Promise<boolean> => {
  if (!userId) {
    const currentUser = await getCurrentAuthUser().catch(() => null);
    userId = currentUser?.id;
    if (!userId) return false;
  }
  
  try {
    debugLogger.log("Checking admin status for user:", userId);
    const isAdmin = await platformApiFetch<boolean>(`/v1/profiles/${userId}/admin-status`);
    
    debugLogger.log("Admin status check result:", isAdmin);
    
    return Boolean(isAdmin);
  } catch (error: unknown) {
    if (!isUnauthorizedPlatformApiError(error)) {
      console.error("Unexpected error in verifyAdmin:", error);
    }
    return false;
  }
};

// Function to verify if a user email is an admin and add them if not
export const verifyAndAddAdmin = async (email: string): Promise<boolean> => {
  try {
    // First get the user ID from the email
    const userData = await fetchUserProfileByEmailWithFields<{ id: string }>(email, "id");
    
    if (!userData) {
      console.error("Error finding user by email");
      return false;
    }
    
    const userId = userData.id;
    
    // Check if user is already an admin using the new function
    const isAdmin = await verifyAdmin(userId);
    
    // If not an admin, add them
    if (!isAdmin) {
      await platformApiFetch(`/v1/admin/users/${userId}/admin`, {
        method: 'PUT',
        body: JSON.stringify({ isAdmin: true }),
      });
      
      debugLogger.log("User successfully added as admin:", email);
      return true;
    }
    
    debugLogger.log("User is already an admin:", email);
    return true;
  } catch (error: unknown) {
    console.error("Unexpected error in verifyAndAddAdmin:", error);
    return false;
  }
};

// This function verifies if the user is admin and redirects if not
export const verify = async (user: MinimalUser | null, navigate: NavigateFn): Promise<boolean> => {
  if (user === null) {
    navigate('/auth');
    return false;
  }
  
  const isAdmin = await verifyAdmin(user?.id);
  debugLogger.log("verify() admin check result:", isAdmin);
  
  if (!isAdmin) {
    navigate('/');
    return false;
  }
  
  return true;
};

export default { verifyAdmin, verifyAndAddAdmin, verify };
